import { useOriginLink } from "../../../app/navigation/return-navigation.js";
import type { AssessmentResult } from "@peer-start/platform-contracts";
import { Card } from "@peer-start/ui";
import { BookOpen, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

export function AssessmentLearningPath({ result }: { result: AssessmentResult }) {
  const { t } = useTranslation();
  const originLink = useOriginLink();
  const groups = [...new Set(result.learningPath.map((part) => part.groupId))];
  return (
    <Card>
      <h2 className="text-lg font-medium">{t("knowledgeAssessment.learningPath")}</h2>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
        {t("knowledgeAssessment.learningPathHelp")}
      </p>
      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        {groups.map((groupId) => {
          const parts = result.learningPath.filter((part) => part.groupId === groupId);
          return (
            <section key={groupId} className="rounded-xl border border-border p-4">
              <h3 className="font-medium">{parts[0]!.groupTitle}</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("knowledgeAssessment.partsRequired", {
                  count: parts.filter((part) => part.required).length,
                  total: parts.length,
                })}
              </p>
              <ul className="mt-3 space-y-3">
                {parts.map((part) => {
                  const Icon = part.required ? BookOpen : CheckCircle2;
                  return (
                    <li key={part.id} className="flex items-start gap-2 text-sm">
                      <Icon
                        aria-hidden="true"
                        className={`mt-0.5 size-4 shrink-0 ${part.required ? "text-primary" : "text-success-foreground"}`}
                      />
                      <div>
                        <p>{part.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {t(part.required ? "knowledgeAssessment.required" : "knowledgeAssessment.exempt")}
                          {!part.tested ? ` · ${t("knowledgeAssessment.untested")}` : ""}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>
      <Link
        className="mt-5 inline-flex rounded-xl bg-primary px-4 py-3 text-sm font-medium text-primary-foreground focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
        data-control-id="assessment.learning"
        to={originLink(`/me/stamps/${result.definition.stampId}#learning`)}
      >
        {t("knowledgeAssessment.openLearning")}
      </Link>
    </Card>
  );
}
